import { useMutation, UseMutationOptions, UseMutationResult } from '@tanstack/react-query';

const API_URL = process.env.API_URL as string;

export interface ResetPasswordRequest {
	email: string;
	/** Always available by the time this is called - the user already picked
	 * a specific workspace before reaching the password step. Workspace-agnostic
	 * reset across organisations is not supported. */
	workspaceId?: string;
}

export class ResetPasswordError extends Error {
	public readonly status: number;

	constructor(message: string, status: number) {
		super(message);
		this.status = status;
	}
}

const doResetPassword = async ({ email, workspaceId }: ResetPasswordRequest): Promise<void> => {
	const response = await fetch(`${API_URL}/resetPassword`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
		body: new URLSearchParams({
			email,
			...(workspaceId ? { workspace_id: workspaceId } : {}),
		}),
	});

	if (!response.ok) {
		throw new ResetPasswordError(`Unable to reset password: ${response.status}`, response.status);
	}
};

export const useDoResetPassword = (
	options?: UseMutationOptions<void, unknown, ResetPasswordRequest, unknown>
): UseMutationResult<void, ResetPasswordError, ResetPasswordRequest, unknown> => {
	return useMutation<void, ResetPasswordError, ResetPasswordRequest, unknown>(
		['resetPassword'],
		(data) => doResetPassword({ email: data.email, workspaceId: data.workspaceId }),
		options
	);
};
